import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * R3F component that renders a pulsing glow ring with orbiting particles
 * around a scene star/tree while AI generation is in progress.
 */
export function GeneratingEffect({
  position,
  color = "#7eb8ff",
  yOffset = 0,
}: {
  position: [number, number, number];
  color?: string;
  yOffset?: number;
}) {
  const ringRef = useRef<THREE.Mesh>(null);
  const particlesRef = useRef<THREE.Points>(null);

  const particlePositions = useMemo(() => {
    const count = 24;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      positions[i * 3] = Math.cos(angle) * 1.8;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = Math.sin(angle) * 1.8;
    }
    return positions;
  }, []);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;

    if (ringRef.current) {
      const pulse = 1 + Math.sin(t * 3) * 0.15;
      ringRef.current.scale.setScalar(pulse);
      const mat = ringRef.current.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.2 + Math.sin(t * 3) * 0.12;
    }

    if (particlesRef.current) {
      particlesRef.current.rotation.y = t * 0.8;
      const geom = particlesRef.current.geometry;
      const posAttr = geom.getAttribute("position") as THREE.BufferAttribute;
      const count = posAttr.count;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + t * 0.8;
        const radiusOscillation = 1.8 + Math.sin(t * 2 + i * 0.5) * 0.3;
        posAttr.setXYZ(
          i,
          Math.cos(angle) * radiusOscillation,
          Math.sin(t * 4 + i * 0.7) * 0.3,
          Math.sin(angle) * radiusOscillation,
        );
      }
      posAttr.needsUpdate = true;

      const pMat = particlesRef.current.material as THREE.PointsMaterial;
      pMat.opacity = 0.4 + Math.sin(t * 2) * 0.2;
    }
  });

  const pos: [number, number, number] = [
    position[0],
    position[1] + yOffset,
    position[2],
  ];

  return (
    <group position={pos}>
      <mesh ref={ringRef} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.5, 1.7, 48]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.25}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      <points ref={particlesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={24}
            array={particlePositions}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          color={color}
          size={0.08}
          transparent
          opacity={0.5}
          toneMapped={false}
          sizeAttenuation
        />
      </points>
    </group>
  );
}
